import { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { useAppConfig } from "../../../AppConfigProvider";
import { usePostHog } from "posthog-js/react";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useLoginMutation } from "@axio/graphql/generated/graphql";
import { useAuth } from "../../../hooks/useAuth";
import LoginForm from "../../../components/auth/LoginForm/LoginForm";
import { Platform } from "react-native";
import { announceForAccessibility } from "../../../utils/accessibility";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import Sentry from "../../../utils/sentry";
import { getDefaultAuthenticatedRoute } from "../../../utils/authRoutes.utils";
import returnToUtils from "../../../utils/returnTo.utils";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RouteProp, useRoute } from "@react-navigation/native";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .transform((val) => val.trim().toLowerCase())
    .pipe(z.string().email("Please enter a valid email")),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type LoginRouteProp = RouteProp<RootStackParamList, "Login">;

function LoginFormContainer({ isSuperAdminRoute }: { isSuperAdminRoute: boolean }) {
  const posthog = usePostHog();
  const { login, isUpdating } = useAuth();
  const { hasSAMLSSO, isCompanionMode, logo, requireSSO, courseModernizerEnabled, courseModernizerOnlyEnabled } =
    useAppConfig();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { dispatch } = useAppState();
  const [loginMutation] = useLoginMutation();
  const { submitHelpTicketLink } = useAppConfig();

  const supportUrl =
    submitHelpTicketLink && submitHelpTicketLink.trim().length > 0 ? submitHelpTicketLink : "https://support.axio.ai";

  const [passwordHidden, setPasswordHidden] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const route = useRoute<RouteProp<RootStackParamList, "Login">>();
  const isInviteFlow = route?.params?.service === "facultyInvitation";

  const email = watch("email");
  const password = watch("password");

  useEffect(() => {
    posthog?.capture("Login Page Visited");
  }, []);

  useEffect(() => {
    if (Platform.OS === "ios" && errorMessage) {
      announceForAccessibility({ message: "Error alert: " + errorMessage });
    }

    if (Platform.OS === "ios" && errors.email) {
      announceForAccessibility({ message: "Please enter valid email" });
    }
  }, [errorMessage, errors.email]);

  useFocusEffect(
    useCallback(() => {
      setErrorMessage("");
      reset({ email: "", password: "" });
    }, [reset])
  );

  const handleFieldChange = useCallback(
    (name: keyof LoginFormData, value: string) => {
      setValue(name, value, { shouldValidate: true, shouldTouch: true });
    },
    [setValue]
  );

  const handlePasswordToggle = useCallback(() => {
    setPasswordHidden((prev) => !prev);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    setErrorMessage("");

    try {
      let result = null;
      let accessToken = null;
      let refreshToken = null;
      let user = null;
      let success = false;

      result = await loginMutation({
        variables: { email: data.email, password: data.password },
      });

      const loginResponse = result.data?.login;

      if (loginResponse) {
        accessToken = loginResponse.accessToken;
        refreshToken = loginResponse.refreshToken;
        user = loginResponse.user;
        success = true;
      }

      if (success) {
        const authResult = await login(accessToken, refreshToken);
        const returnToUrl = await returnToUtils.getAndClearReturnToUrl();
        setErrorMessage("");
        dispatch({
          type: actions.SET_META,
          payload: { user, email: data.email },
        });
        const isVerified = authResult.isEmailVerified && authResult.otpVerifiedForSession;

        if (!isVerified) {
          navigation.navigate("Verify Email");
          return;
        }

        if (returnToUrl) {
          window.location.href = returnToUrl;
          return;
        }

        const redirect = getDefaultAuthenticatedRoute({
          userRole: authResult.userRole,
          isCompanionMode,
          courseModernizerOnlyEnabled: !!courseModernizerEnabled && !!courseModernizerOnlyEnabled,
        });
        navigation.reset({
          index: 0,
          routes: [{ name: redirect }],
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(error.message);
    }
  };

  const handleResetPasswordPress = useCallback(() => {
    navigation.navigate("Reset Password");
  }, [navigation]);

  const handleSignupPress = useCallback(() => {
    navigation.navigate("Signup");
  }, [navigation]);

  const handleSAMLSSOPress = useCallback(async () => {
    const redirect = await AsyncStorage.getItem("axio_return_to");
    const baseUrl = "/api/sso/login";

    if (redirect && returnToUtils.isValidInternalUrl(redirect)) {
      const encodedRedirect = encodeURIComponent(redirect);
      window.location.href = `${baseUrl}?redirectUrl=${encodedRedirect}`;
    } else {
      window.location.href = baseUrl;
    }
  }, []);

  return (
    <LoginForm
      email={email}
      password={password}
      emailError={errors.email?.message}
      passwordError={errors.password?.message}
      errorMessage={errorMessage}
      logo={logo}
      passwordHidden={passwordHidden}
      onEmailChangeText={(text) => handleFieldChange("email", text)}
      onPasswordChangeText={(text) => handleFieldChange("password", text)}
      onEmailBlur={() => handleFieldChange("email", email)}
      onPasswordBlur={() => handleFieldChange("password", password)}
      onPasswordToggle={handlePasswordToggle}
      onSubmit={handleSubmit(onSubmit)}
      onResetPasswordPress={handleResetPasswordPress}
      onSignupPress={handleSignupPress}
      isLoading={isUpdating}
      hasSAMLSSO={hasSAMLSSO}
      onSAMLSSOPress={handleSAMLSSOPress}
      requireSSO={!isSuperAdminRoute && requireSSO}
      supportUrl={supportUrl}
      isInviteFlow={isInviteFlow}
    />
  );
}

export default LoginFormContainer;
